import React from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';
import { fadeInUp } from '../../utils/animations';
import { trackContactFormSubmit } from '../../utils/analytics';
import { useTranslation } from 'react-i18next';
import { announceToScreenReader } from '../../utils/accessibility';

const Container = styled.div`
display: flex;
flex-direction: column;
justify-content: center;
position: relative;
z-index: 1;
align-items: center;
@media (max-width: 960px) {
    padding: 0px;
}
`

const Wrapper = styled.div`
position: relative;
display: flex;
justify-content: space-between;
align-items: center;
flex-direction: column;
width: 100%;
max-width: 1350px;
padding: 0px 0px 80px 0px;
gap: 12px;
@media (max-width: 960px) {
    flex-direction: column;
}
`

const Title = styled.h2`
font-size: 42px;
text-align: center;
font-weight: 600;
margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
      margin-top: 12px;
      font-size: 32px;
  }
`;

const Desc = styled.p`
    font-size: 18px;
    text-align: center;
    max-width: 600px;
    color: ${({ theme }) => theme.text_secondary};
    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 16px;
    }
`;


const ContactForm = styled.form`
  width: 95%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.card};
  padding: 32px;
  border-radius: 16px;
  box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
  margin-top: 28px;
  gap: 12px;
`

const ContactTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 6px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
`;

const ContactInput = styled.input`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`

const ContactInputMessage = styled.textarea`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`

const ContactButton = styled.button`
  width: 100%;
  text-align: center;
  background: hsla(271, 100%, 50%, 1);
  background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  padding: 13px 16px;
  margin-top: 2px;
  border-radius: 12px;
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(133, 76, 230, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 11px 14px;
    font-size: 16px;
  }
`

const Honeypot = styled.input`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;



const Contact = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.current.website?.value) {
      return;
    }

    const email = form.current.from_email.value.trim();
    const name = form.current.from_name.value.trim();
    const subject = form.current.subject.value.trim();
    const message = form.current.message.value.trim();

    if (!email || !name || !subject || !message) {
      const msg = t('contact.validation.allFields');
      toast.error(msg);
      announceToScreenReader(msg, 'assertive');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      const msg = t('contact.validation.validEmail');
      toast.error(msg);
      announceToScreenReader(msg, 'assertive');
      return;
    }

    if (name.length < 2) {
      const msg = t('contact.validation.nameLength');
      toast.error(msg);
      announceToScreenReader(msg, 'assertive');
      return;
    }

    if (message.length < 10) {
      const msg = t('contact.validation.messageLength');
      toast.error(msg);
      announceToScreenReader(msg, 'assertive');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading(t('contact.sending'));

    try {
      await emailjs.sendForm(
        'service_8nqpjku',
        'template_qzsccvl',
        form.current,
        '4M2p1GILelfcPLWHd'
      );
      
      toast.dismiss(loadingToast);
      const successMsg = t('contact.success');
      toast.success(successMsg);
      announceToScreenReader(successMsg);
      trackContactFormSubmit();
      form.current.reset();
    } catch (error) {
      toast.dismiss(loadingToast);
      const errorMsg = t('contact.error');
      toast.error(errorMsg);
      announceToScreenReader(errorMsg, 'assertive');
      console.error('EmailJS Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container id="contact">
      <Wrapper>
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <Title>{t('contact.title')}</Title>
        </motion.div>
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ delay: 0.1 }}
        >
          <Desc>{t('contact.description')}</Desc>
        </motion.div>
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ delay: 0.2 }}
        >
          <ContactForm ref={form} onSubmit={handleSubmit} noValidate>
          <ContactTitle>{t('contact.formTitle')}</ContactTitle>
          <Honeypot type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <FieldGroup>
            <Label htmlFor="contact-email">{t('contact.email')}</Label>
            <ContactInput
              id="contact-email"
              placeholder={t('contact.email')}
              name="from_email"
              type="email"
              autoComplete="email"
              required
              aria-required="true"
            />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="contact-name">{t('contact.name')}</Label>
            <ContactInput
              id="contact-name"
              placeholder={t('contact.name')}
              name="from_name"
              autoComplete="name"
              required
              aria-required="true"
            />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="contact-subject">{t('contact.subject')}</Label>
            <ContactInput
              id="contact-subject"
              placeholder={t('contact.subject')}
              name="subject"
              required
              aria-required="true"
            />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="contact-message">{t('contact.message')}</Label>
            <ContactInputMessage
              id="contact-message"
              placeholder={t('contact.message')}
              rows="4"
              name="message"
              required
              aria-required="true"
            />
          </FieldGroup>
          <ContactButton type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? t('contact.sending') : t('contact.send')}
          </ContactButton>
        </ContactForm>
        </motion.div>
      </Wrapper>
    </Container>
  );
};

export default Contact;
